import { useEffect, useState, useCallback } from "react";
import { Plus, FileText, Upload, Check } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { FORM_STATUS, fmtDate } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const EMPTY = { form_type: "", title: "", description: "", start_date: "", end_date: "", attachment: null, sick_note: null };

// Read a file to base64; downscale images to keep payload small.
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(file);
      return;
    }
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const max = 1400;
      let { width, height } = img;
      if (width > max || height > max) {
        const s = Math.min(max / width, max / height);
        width = Math.round(width * s); height = Math.round(height * s);
      }
      const c = document.createElement("canvas");
      c.width = width; c.height = height;
      c.getContext("2d").drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      resolve(c.toDataURL("image/jpeg", 0.72));
    };
    img.onerror = reject;
    img.src = url;
  });
}

export default function Forms() {
  const [forms, setForms] = useState([]);
  const [types, setTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/forms/me").then((r) => setForms(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/forms/types").then((r) => setTypes(r.data)).catch(() => {});
    load();
  }, [load]);

  const pick = async (key, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error("Ukuran file maksimal 8MB");
    try {
      const b64 = await fileToBase64(file);
      setF((prev) => ({ ...prev, [key]: b64 }));
      toast.success("Lampiran siap");
    } catch { toast.error("Gagal membaca file"); }
  };

  const submit = async () => {
    if (!f.form_type || !f.title) return toast.error("Jenis dan judul formulir wajib diisi");
    if (!f.attachment) return toast.error("Wajib upload scan formulir hard copy yang sudah ditandatangani");
    if (f.form_type === "SAKIT" && !f.sick_note) return toast.error("Pengajuan sakit wajib melampirkan bukti surat sakit dokter");
    setSaving(true);
    try {
      await api.post("/forms", f);
      toast.success("Formulir berhasil diajukan");
      setOpen(false);
      setF(EMPTY);
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight lg:text-3xl">Formulir Saya</h1>
          <p className="mt-1 text-sm text-slate-500">Ajukan permohonan dengan lampiran scan formulir hard copy.</p>
        </div>
        <Button data-testid="new-form-button" onClick={() => setOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" /> Ajukan Formulir
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {forms.length === 0 && (
          <Card className="col-span-full flex flex-col items-center gap-2 rounded-2xl border-dashed p-10 text-center text-slate-400">
            <FileText className="h-8 w-8" />
            <p>Belum ada formulir yang diajukan.</p>
          </Card>
        )}
        {forms.map((form) => (
          <Card key={form.id} data-testid="my-form-card" className="rounded-2xl border-slate-200 p-5 dark:border-slate-800">
            <div className="flex items-start justify-between gap-2">
              <div>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">{form.form_type_label}</span>
                <p className="mt-2 font-semibold">{form.title}</p>
                <p className="font-mono text-[11px] text-slate-400">{form.form_number}</p>
              </div>
              <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${FORM_STATUS[form.status]?.cls}`}>
                {FORM_STATUS[form.status]?.label || form.status}
              </span>
            </div>
            {form.description && <p className="mt-2 text-sm text-slate-500">{form.description}</p>}
            <div className="mt-3 flex flex-wrap items-center gap-3">
              {form.attachment && <a href={form.attachment} target="_blank" rel="noreferrer" data-testid="form-attachment-link" className="inline-flex items-center gap-1 text-xs font-medium text-primary underline"><FileText className="h-3 w-3" /> Scan Formulir</a>}
              {form.sick_note && <a href={form.sick_note} target="_blank" rel="noreferrer" data-testid="form-sicknote-link" className="inline-flex items-center gap-1 text-xs font-medium text-rose-600 underline"><FileText className="h-3 w-3" /> Surat Sakit</a>}
            </div>
            <div className="mt-3 flex items-center justify-between text-xs text-slate-400">
              <span>Diajukan: {fmtDate(form.created_at)}</span>
              <span>Tahap: {form.current_stage}</span>
            </div>
            {form.reviews?.length > 0 && (
              <div className="mt-3 space-y-1 border-t border-slate-100 pt-3 dark:border-slate-800">
                {form.reviews.map((r, i) => (
                  <p key={i} className="text-xs text-slate-500">
                    <span className={r.action === "APPROVE" ? "text-emerald-600" : "text-rose-600"}>{r.action === "APPROVE" ? "Disetujui" : "Ditolak"}</span> oleh {r.role}{r.note ? ` — "${r.note}"` : ""}
                  </p>
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Ajukan Formulir HR</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Jenis Formulir</Label>
              <Select value={f.form_type} onValueChange={(v) => setF({ ...f, form_type: v })}>
                <SelectTrigger data-testid="hr-form-type-select"><SelectValue placeholder="Pilih jenis" /></SelectTrigger>
                <SelectContent>
                  {types.map((t) => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Judul / Perihal</Label>
              <Input data-testid="hr-form-title-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Contoh: Izin sakit 2 hari" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Tanggal Mulai</Label>
                <Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Tanggal Selesai</Label>
                <Input type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Keterangan</Label>
              <Textarea data-testid="hr-form-desc-input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Jelaskan permohonan Anda" />
            </div>

            <div className="space-y-1.5 rounded-xl border border-dashed border-slate-300 p-3 dark:border-slate-700">
              <Label className="flex items-center gap-1 text-xs font-semibold">Scan Formulir Hard Copy <span className="text-rose-500">*wajib</span></Label>
              <p className="text-[11px] text-slate-400">Upload foto/scan formulir fisik yang sudah ditandatangani.</p>
              <div className="flex items-center gap-2">
                <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200">
                  <Upload className="h-3.5 w-3.5" /> Pilih File
                  <input data-testid="form-attachment-input" type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => pick("attachment", e.target.files?.[0])} />
                </label>
                {f.attachment && <span className="inline-flex items-center gap-1 text-xs text-emerald-600"><Check className="h-3.5 w-3.5" /> terlampir</span>}
              </div>
            </div>

            {f.form_type === "SAKIT" && (
              <div className="space-y-1.5 rounded-xl border border-dashed border-rose-300 p-3 dark:border-rose-800">
                <Label className="flex items-center gap-1 text-xs font-semibold">Bukti Surat Sakit <span className="text-rose-500">*wajib</span></Label>
                <p className="text-[11px] text-slate-400">Tanpa surat sakit, ketidakhadiran dihitung sebagai Alpha.</p>
                <div className="flex items-center gap-2">
                  <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-rose-50 px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-100 dark:bg-rose-950 dark:text-rose-200">
                    <Upload className="h-3.5 w-3.5" /> Pilih File
                    <input data-testid="form-sicknote-input" type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => pick("sick_note", e.target.files?.[0])} />
                  </label>
                  {f.sick_note && <span className="inline-flex items-center gap-1 text-xs text-emerald-600"><Check className="h-3.5 w-3.5" /> terlampir</span>}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button data-testid="hr-form-submit-button" disabled={saving} onClick={submit}>{saving ? "Mengirim..." : "Ajukan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
